import { Component, Input, Output, EventEmitter, inject, signal, HostListener, computed } from '@angular/core';
import { DatePipe } from '@angular/common';
import { AuthService } from '../../../../core/auth/auth.service';
import { NotificationService, Notification as AppNotification } from '../../../../core/services/notification.service';
import { LlmStatusService } from '../../../../core/services/llm-status.service';
import { SystemMonitorComponent } from './system-monitor.component';
import { ScraperAlertIndicatorComponent } from './scraper-alert-indicator.component';
import { ResearchSelectorComponent } from './research-selector.component';
import { ChangePasswordDialogComponent } from '../../ui/change-password-dialog/change-password-dialog.component';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [DatePipe, SystemMonitorComponent, ScraperAlertIndicatorComponent, ResearchSelectorComponent, ChangePasswordDialogComponent],
  styles: [`
    .llm-dot { position: relative; cursor: pointer; }
    .llm-dot .llm-tooltip {
      display: none;
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      min-width: 200px;
      background: #0d1117;
      border: 1px solid #1f2937;
      border-radius: 10px;
      padding: 10px 12px;
      font-size: 11px;
      color: #d1d5db;
      z-index: 60;
      white-space: nowrap;
      box-shadow: 0 8px 30px rgba(0,0,0,0.6);
    }
    .llm-dot:hover .llm-tooltip { display: block; }
  `],
  template: `
    <header class="flex items-center justify-between gap-4 h-[60px] px-5 border-b border-[var(--color-surface-border)] shrink-0 relative z-20"
            style="background: linear-gradient(90deg, #0d1117 0%, #030712 100%)">

      <!-- Hamburger (mobile only) -->
      @if (showMenuBtn) {
        <button (click)="menuToggled.emit()"
          class="flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-gray-100 hover:bg-[var(--color-surface-hover)] transition-colors shrink-0 mr-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
          </svg>
        </button>
      }

      <!-- Left: research selector -->
      <div class="flex items-center gap-3 min-w-0">
        @if (showResearchSelector()) {
          <app-research-selector />
        }
      </div>

      <!-- Right: actions -->
      <div class="flex items-center gap-2 shrink-0">

        <!-- Host CPU / RAM / GPU -->
        <app-system-monitor />

        <!-- Divider -->
        <div class="hidden md:block w-px h-6 bg-[var(--color-surface-border)]"></div>

        <!-- LLM Status Indicator -->
        <div class="llm-dot flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
             (click)="llmStatus.check()">

          @if (llmStatus.checking()) {
            <!-- Pulsing while checking -->
            <span class="w-2 h-2 rounded-full bg-zinc-500 animate-pulse"></span>
          } @else if (llmStatus.status() === 'online') {
            <span class="relative flex w-2 h-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-50"></span>
              <span class="relative inline-flex rounded-full w-2 h-2 bg-green-400"></span>
            </span>
          } @else if (llmStatus.status() === 'offline') {
            <span class="w-2 h-2 rounded-full bg-red-500"></span>
          } @else {
            <span class="w-2 h-2 rounded-full bg-zinc-600"></span>
          }

          <span class="text-[11px] font-medium hidden sm:block"
                [class.text-green-400]="llmStatus.status() === 'online'"
                [class.text-red-400]="llmStatus.status() === 'offline'"
                [class.text-zinc-500]="llmStatus.status() === 'unknown'">
            LLM
          </span>

          <!-- Tooltip -->
          <div class="llm-tooltip">
            <div class="flex items-center gap-2 mb-2">
              @if (llmStatus.status() === 'online') {
                <span class="w-2 h-2 rounded-full bg-green-400 shrink-0"></span>
                <span class="font-semibold text-green-400">Online</span>
              } @else if (llmStatus.status() === 'offline') {
                <span class="w-2 h-2 rounded-full bg-red-500 shrink-0"></span>
                <span class="font-semibold text-red-400">Offline</span>
              } @else {
                <span class="w-2 h-2 rounded-full bg-zinc-600 shrink-0"></span>
                <span class="font-semibold text-zinc-400">Checking…</span>
              }
            </div>
            @if (llmStatus.health()) {
              <div class="text-zinc-400 space-y-0.5">
                <div>Model: <span class="text-zinc-200">{{ llmStatus.health()!.model }}</span></div>
                @if (llmStatus.health()!.latency_ms !== null) {
                  <div>Latency: <span class="text-zinc-200">{{ llmStatus.health()!.latency_ms }} ms</span></div>
                }
                @if (!llmStatus.health()!.model_available) {
                  <div class="text-yellow-400 mt-1">Model not loaded on server</div>
                }
                @if (llmStatus.health()!.error) {
                  <div class="text-red-400 mt-1">{{ llmStatus.health()!.error }}</div>
                }
              </div>
            }
            @if (llmStatus.lastCheck()) {
              <div class="text-zinc-600 mt-2 text-[10px]">
                Last check: {{ llmStatus.lastCheck() | date:'HH:mm:ss' }}
              </div>
            }
            <div class="text-zinc-600 text-[10px]">Click to refresh · auto-checks every 4h</div>
          </div>
        </div>

        <!-- Scraper Alerts -->
        <app-scraper-alert-indicator />

        <!-- Divider -->
        <div class="w-px h-6 bg-[var(--color-surface-border)]"></div>

        <!-- Notifications Bell -->
        <div class="relative">
          <button
            (click)="toggleNotifPanel()"
            class="relative flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-gray-100 hover:bg-[var(--color-surface-hover)] transition-colors">
            <i class="fa-solid fa-bell text-base"></i>
            @if (notifService.unreadCount() > 0) {
              <span class="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold text-white leading-none"
                    style="background: linear-gradient(135deg,#06b6d4,#3b82f6)">
                {{ notifService.unreadCount() > 9 ? '9+' : notifService.unreadCount() }}
              </span>
            }
          </button>

          <!-- Notification Panel -->
          @if (notifOpen()) {
            <div class="absolute right-0 top-full mt-2 w-80 rounded-xl border border-[var(--color-surface-border)] bg-[var(--color-surface-raised)] shadow-2xl animate-fade-in overflow-hidden">
              <div class="flex items-center justify-between px-4 py-3 border-b border-[var(--color-surface-border)]">
                <span class="text-sm font-semibold text-zinc-100">Notifications</span>
                @if (notifService.unreadCount() > 0) {
                  <button (click)="notifService.markAllRead()" class="text-xs text-cyan-400 hover:text-cyan-300 transition-colors">
                    Mark all read
                  </button>
                }
              </div>
              <div class="max-h-72 overflow-y-auto">
                @if (notifService.items().length === 0) {
                  <div class="flex flex-col items-center justify-center py-8 text-zinc-500">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M15 17H5l2-2V11a5 5 0 0 1 10 0v4l2 2H15z"/>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                    <p class="text-xs">No notifications</p>
                  </div>
                }
                @for (n of notifService.items(); track n.id) {
                  <div
                    (click)="notifService.markRead(n.id)"
                    class="flex gap-3 px-4 py-3 hover:bg-[var(--color-surface-hover)] cursor-pointer border-b border-[var(--color-surface-border)]/50 transition-colors"
                    [class.opacity-50]="n.read">
                    <div [class]="dotClass(n.type)" class="mt-1 shrink-0 w-2 h-2 rounded-full"></div>
                    <div class="min-w-0">
                      <p class="text-xs font-semibold text-zinc-200 leading-tight">{{ n.title }}</p>
                      <p class="text-xs text-zinc-400 mt-0.5 leading-relaxed">{{ n.message }}</p>
                      <p class="text-[10px] text-zinc-600 mt-1">{{ n.timestamp | date:'HH:mm' }}</p>
                    </div>
                  </div>
                }
              </div>
              @if (notifService.items().length > 0) {
                <div class="px-4 py-2 border-t border-[var(--color-surface-border)]">
                  <button (click)="notifService.clear()" class="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
                    Clear all
                  </button>
                </div>
              }
            </div>
          }
        </div>

        <!-- Divider -->
        <div class="w-px h-6 bg-[var(--color-surface-border)]"></div>

        <!-- User menu -->
        <div class="relative">
          <button
            (click)="toggleUserMenu()"
            class="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors group">
            <div class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                 style="background: linear-gradient(135deg,#06b6d4,#2563eb)">
              {{ initial() }}
            </div>
            <span class="text-sm text-zinc-300 group-hover:text-zinc-100 hidden sm:block">{{ username() }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 text-zinc-500 transition-transform duration-150 hidden sm:block"
                 [class.rotate-180]="userMenuOpen()"
                 fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          @if (userMenuOpen()) {
            <div class="absolute right-0 top-full mt-2 w-48 rounded-xl border border-[var(--color-surface-border)] bg-[var(--color-surface-raised)] shadow-2xl animate-fade-in overflow-hidden">
              <div class="px-4 py-3 border-b border-[var(--color-surface-border)]">
                <p class="text-xs font-semibold text-zinc-200">{{ username() }}</p>
                <p class="text-xs text-zinc-500 capitalize">{{ role() }}</p>
              </div>
              <div class="p-1">
                <button
                  (click)="openChangePassword()"
                  class="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-300 hover:bg-[var(--color-surface-hover)] hover:text-zinc-100 transition-colors">
                  <i class="fa-solid fa-key w-4 text-center"></i>
                  Change password
                </button>
                <button
                  (click)="logout()"
                  class="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Sign out
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </header>

    <app-change-password-dialog [(open)]="changePwOpen" />
  `,
})
export class TopbarComponent {
  @Input() title = 'Dashboard';
  @Input() showMenuBtn = false;
  @Output() menuToggled = new EventEmitter<void>();

  auth        = inject(AuthService);
  notifService = inject(NotificationService);
  llmStatus   = inject(LlmStatusService);

  notifOpen    = signal(false);
  userMenuOpen = signal(false);
  changePwOpen = signal(false);

  username = computed(() => this.auth.currentUser()?.username ?? 'Guest');
  role     = computed(() => this.auth.currentUser()?.role ?? '');
  initial  = computed(() => (this.auth.currentUser()?.username ?? 'G')[0].toUpperCase());
  showResearchSelector = computed(() => this.auth.isSuperAdmin || this.auth.isResearcher);

  toggleNotifPanel(): void {
    this.notifOpen.update(v => !v);
    this.userMenuOpen.set(false);
  }

  toggleUserMenu(): void {
    this.userMenuOpen.update(v => !v);
    this.notifOpen.set(false);
  }

  openChangePassword(): void {
    this.userMenuOpen.set(false);
    this.changePwOpen.set(true);
  }

  logout(): void {
    this.userMenuOpen.set(false);
    this.auth.logout();
  }

  dotClass(type: string): string {
    const map: Record<string, string> = {
      success: 'bg-green-400',
      error:   'bg-red-400',
      warning: 'bg-yellow-400',
      info:    'bg-sky-400',
    };
    return map[type] ?? 'bg-zinc-400';
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    const el = e.target as HTMLElement;
    if (!el.closest('app-topbar')) {
      this.notifOpen.set(false);
      this.userMenuOpen.set(false);
    }
  }
}
